import React, { useMemo, useState } from 'react';
import type { Player } from '../../types/player';
import type { LeagueFormatType } from '../LeagueFormatSelector';
import { PlayerPhoto } from '../PlayerPhoto';
import { Surface } from '../ui/Surface';
import {
  assignRosterSlots,
  formatLabel,
  recommendPicks,
  rosterConfigForFormat,
  upcomingPicks,
} from '../../utils/draftBoard';

interface RecentPick {
  pick: number;
  teamId: number;
  playerName: string;
  position: string;
  team: string;
  timestamp: Date;
}

interface NewDraftExperienceProps {
  leagueFormat: LeagueFormatType;
  teamCount: number;
  draftPosition: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  draftState: {
    currentPick: number;
    userTeamId: number;
    userRoster: Player[];
    availablePlayers: Player[];
    draftedPlayers: Set<string>;
    recentPicks: RecentPick[];
    draftStatus: 'inactive' | 'starting' | 'active' | 'ending';
  };
  currentTeam: number;
  round: number;
  isUserTurn: boolean;
  onDraftPlayer: (player: Player) => void;
  onLeaveDraft: () => void;
}

const NewDraftExperience: React.FC<NewDraftExperienceProps> = ({
  leagueFormat,
  teamCount,
  draftPosition,
  searchQuery,
  setSearchQuery,
  draftState,
  currentTeam,
  round,
  isUserTurn,
  onDraftPlayer,
  onLeaveDraft,
}) => {
  const [mobileView, setMobileView] = useState<'board' | 'roster'>('board');
  const [positionFilter, setPositionFilter] = useState('All');
  const { currentPick, userRoster, availablePlayers, recentPicks } = draftState;
  const config = rosterConfigForFormat(leagueFormat);
  const assigned = useMemo(() => assignRosterSlots(userRoster, leagueFormat), [userRoster, leagueFormat]);
  const nextUp = useMemo(
    () => upcomingPicks(currentPick, teamCount, draftPosition, 4),
    [currentPick, teamCount, draftPosition]
  );
  const recommendations = useMemo(
    () => recommendPicks(availablePlayers, userRoster, leagueFormat, 3),
    [availablePlayers, userRoster, leagueFormat]
  );

  const filteredPlayers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return availablePlayers.filter((player) => {
      const positionMatch = positionFilter === 'All' || player.position === positionFilter;
      if (!positionMatch) return false;
      if (!query) return true;
      return (
        player.name.toLowerCase().includes(query) ||
        player.position.toLowerCase().includes(query) ||
        (player.team && player.team.toLowerCase().includes(query))
      );
    });
  }, [availablePlayers, searchQuery, positionFilter]);

  const handleDraftPlayer = (player: Player) => {
    if (!isUserTurn) return;
    onDraftPlayer(player);
  };

  return (
    <div className="min-h-screen bg-ink text-zinc-100">
      <div className="pointer-events-none absolute inset-0 app-glow opacity-70" />
      <div className="relative mx-auto max-w-7xl px-4 py-6 lg:px-8">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-fiesta-yellow">
              {formatLabel(leagueFormat)} · {teamCount} teams · seat {draftPosition}
            </p>
            <h1 className="font-display text-2xl font-semibold text-white">
              Pick {currentPick} · Round {round}
            </h1>
            <p className="mt-1 text-sm text-zinc-400">
              {isUserTurn ? 'Your pick' : `Team ${currentTeam} is on the clock`}
              {nextUp.length > 0 ? (
                <span>
                  {' · next '}
                  {nextUp.map((pick, index) => (
                    <span key={pick.pick}>
                      {index > 0 ? ', ' : ''}
                      {pick.yours ? 'you' : `T${pick.team}`}
                    </span>
                  ))}
                </span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onLeaveDraft}
            className="self-start rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300 hover:border-white/20"
          >
            Leave draft
          </button>
        </div>

        <div className="mb-4 lg:hidden">
          <div className="grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-white/5 p-1">
            {(['board', 'roster'] as const).map((view) => (
              <button
                key={view}
                type="button"
                onClick={() => setMobileView(view)}
                className={`rounded-lg py-2 text-sm font-medium ${
                  mobileView === view ? 'bg-fiesta-yellow text-ink' : 'text-zinc-400'
                }`}
              >
                {view === 'board' ? 'Board' : 'Roster'}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className={mobileView === 'roster' ? 'hidden lg:block' : 'block'}>
            {isUserTurn && recommendations.length > 0 ? (
              <div className="mb-4 grid gap-2 sm:grid-cols-3">
                {recommendations.map((rec) => (
                  <button
                    key={String(rec.player.id)}
                    type="button"
                    onClick={() => handleDraftPlayer(rec.player)}
                    className="rounded-2xl border border-fiesta-yellow/30 bg-fiesta-yellow/10 p-3 text-left hover:border-fiesta-yellow/60"
                  >
                    <p className="text-xs text-fiesta-yellow">{rec.reason}</p>
                    <p className="mt-1 font-medium text-white">{rec.player.name}</p>
                    <p className="text-xs text-zinc-400">
                      {rec.player.position} · {rec.player.team} · #{rec.player.rank}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <Surface className="mb-4 px-4 py-3 text-sm text-zinc-400">
                {isUserTurn ? 'Pick a player below.' : `Waiting on team ${currentTeam}.`}
              </Surface>
            )}

            <Surface className="p-4">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row">
                <input
                  type="text"
                  placeholder="Search the board"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder:text-zinc-500"
                />
                <div className="flex flex-wrap gap-1.5">
                  {['All', 'QB', 'RB', 'WR', 'TE', 'K', 'DEF'].map((pos) => (
                    <button
                      key={pos}
                      type="button"
                      onClick={() => setPositionFilter(pos)}
                      className={`rounded-full border px-3 py-1.5 text-xs ${
                        positionFilter === pos
                          ? 'border-fiesta-yellow bg-fiesta-yellow text-ink'
                          : 'border-white/10 text-zinc-300'
                      }`}
                    >
                      {pos === 'DEF' ? 'D/ST' : pos}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                {filteredPlayers.slice(0, 80).map((player) => (
                  <button
                    key={String(player.id)}
                    type="button"
                    disabled={!isUserTurn}
                    onClick={() => handleDraftPlayer(player)}
                    className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left ${
                      isUserTurn
                        ? 'border-white/10 bg-white/[0.03] hover:border-fiesta-yellow/50'
                        : 'cursor-not-allowed border-white/5 bg-white/[0.02] opacity-70'
                    }`}
                  >
                    <span className="w-8 text-center text-xs text-zinc-500">{player.rank}</span>
                    <PlayerPhoto
                      name={player.name}
                      position={player.position}
                      team={player.team}
                      sleeperId={player.id}
                      espnId={player.espn_id}
                      imageUrl={player.imageUrl}
                      className="h-10 w-10 rounded-full object-cover bg-zinc-800"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-white">{player.name}</span>
                      <span className="block text-xs text-zinc-500">
                        {player.team} · {player.position === 'DEF' ? 'D/ST' : player.position}
                        {player.insight ? ` · ${player.insight}` : ''}
                      </span>
                    </span>
                    <span className="text-right text-sm text-zinc-300">
                      {Math.round(player.projection || player.projectedPoints || 0)}
                    </span>
                  </button>
                ))}
              </div>
            </Surface>
          </div>

          <div className={`space-y-4 ${mobileView === 'board' ? 'hidden lg:block' : 'block'}`}>
            <Surface className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-medium text-white">Your roster</h2>
                <span className="text-xs text-zinc-500">
                  {userRoster.length} of {config.positions.reduce((sum, pos) => sum + pos.count, 0) + config.benchSize}
                </span>
              </div>
              <div className="max-h-[28rem] space-y-3 overflow-y-auto pr-1">
                {config.positions.map((spec) => (
                  <div key={spec.name}>
                    <p className="mb-1 text-xs text-zinc-500">{spec.name}</p>
                    <div className="space-y-1">
                      {(assigned.starters[spec.name] || []).map((player, index) => (
                        <div
                          key={`${spec.name}-${index}`}
                          className="flex min-h-[40px] items-center rounded-lg border border-white/10 bg-black/20 px-2 py-1.5"
                        >
                          {player ? (
                            <div className="flex w-full items-center gap-2">
                              <PlayerPhoto
                                name={player.name}
                                position={player.position}
                                team={player.team}
                                sleeperId={player.id}
                                espnId={player.espn_id}
                                imageUrl={player.imageUrl}
                                className="h-6 w-6 rounded-full object-cover"
                              />
                              <span className="truncate text-xs text-white">{player.name}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-zinc-600">Open</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div>
                  <p className="mb-1 text-xs text-zinc-500">Bench</p>
                  {Array.from({ length: Math.min(config.benchSize, Math.max(assigned.bench.length, 3)) }).map((_, index) => {
                    const player = assigned.bench[index];
                    return (
                      <div
                        key={`bench-${index}`}
                        className="mb-1 flex min-h-[40px] items-center rounded-lg border border-white/10 bg-black/20 px-2 py-1.5"
                      >
                        {player ? (
                          <span className="truncate text-xs text-white">{player.name}</span>
                        ) : (
                          <span className="text-xs text-zinc-600">Open</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Surface>

            <Surface className="p-4">
              <h2 className="mb-3 font-medium text-white">Recent picks</h2>
              {recentPicks.length === 0 ? (
                <p className="text-sm text-zinc-500">Picks will land here.</p>
              ) : (
                <div className="space-y-2">
                  {recentPicks.slice(0, 8).map((pick) => (
                    <div key={`${pick.pick}-${pick.playerName}`} className="flex items-center gap-2 text-sm">
                      <span className="w-7 text-xs text-zinc-500">{pick.pick}</span>
                      <span className="min-w-0 flex-1 truncate text-white">{pick.playerName}</span>
                      <span className="text-xs text-zinc-500">
                        {pick.teamId === draftPosition ? 'You' : `T${pick.teamId}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Surface>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewDraftExperience;
